import { AuthContext, StaffRole } from '@urbanx/agx.auth.client'
import { Pages } from 'constants/menu'
import CampaignsIcon from 'components/sidebar/icons/campaigns-icon'
import { ContentLayout, PageLayout } from 'layout'
import { useCallback, useContext, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import CampaignsDashboardHeader from './CampaignsDashboardHeader'
import CampaignsDashboardTable from './CampaignsDashboardTable'
import useCampaignsDashboard from './useCampaignsDashboard'

const CampaignsDashboard = () => {
  const navigate = useNavigate()
  const { userAccount } = useContext(AuthContext)
  const userRoles = (userAccount?.roles as StaffRole[]) || []
  const {
    rows,
    agencyLookup,
    isPending,
    isAgenciesPending,
    lastRowRef,
    addressSearchText,
    agencyFilterOptions,
    selectedCampaignStatusOptions,
    selectedContractStatusOptions,
    selectedLaunchDateStatusOptions,
    selectedMarketingStatusOptions,
    setAddressSearchText,
    handleAgencyFilterSelect,
    handleCampaignStatusFilterSelect,
    handleContractFilterSelect,
    handleLaunchDateFilterSelect,
    handleMarketingFilterSelect,
    resetAgencyFilters,
    resetCampaignStatusFilters,
    resetContractFilters,
    resetLaunchDateFilters,
    resetMarketingFilters,
    onSortLaunch,
  } = useCampaignsDashboard()

  const onCampaignRowClick = useCallback(
    (index: number) => {
      const campaign = rows[index]
      if (!campaign?.campaignId || !campaign.agencyId) return

      const page = userRoles.includes(StaffRole.WorkflowContributor)
        ? Pages.CAMPAIGN_TASKS
        : Pages.PROPERTY_INFORMATION

      navigate(
        `/${Pages.AGENCIES}/${campaign.agencyId}/${Pages.CAMPAIGNS}/${campaign.campaignId}/${page}`
      )
    },
    [navigate, rows, userRoles]
  )

  const userName = useMemo(() => {
    return [userAccount?.firstName, userAccount?.lastName]
      .filter((namePart) => namePart != null && namePart.length > 0)
      .join(' ')
  }, [userAccount])

  return (
    <PageLayout
      agentName={userName}
      primaryPageTitle="Campaigns"
      pageIcon={
        <div className="iconContainer">
          <CampaignsIcon height="28" width="28" />
        </div>
      }
      header={
        <CampaignsDashboardHeader
          addressSearchText={addressSearchText}
          agencyFilterOptions={agencyFilterOptions}
          selectedCampaignStatusOptions={selectedCampaignStatusOptions}
          selectedContractStatusOptions={selectedContractStatusOptions}
          selectedLaunchDateStatusOptions={selectedLaunchDateStatusOptions}
          selectedMarketingStatusOptions={selectedMarketingStatusOptions}
          onAddressSearchTextChange={setAddressSearchText}
          handleAgencyFilterSelect={handleAgencyFilterSelect}
          handleCampaignStatusFilterSelect={handleCampaignStatusFilterSelect}
          handleContractFilterSelect={handleContractFilterSelect}
          handleLaunchDateFilterSelect={handleLaunchDateFilterSelect}
          handleMarketingFilterSelect={handleMarketingFilterSelect}
          resetAgencyFilters={resetAgencyFilters}
          resetCampaignStatusFilters={resetCampaignStatusFilters}
          resetContractFilters={resetContractFilters}
          resetLaunchDateFilters={resetLaunchDateFilters}
          resetMarketingFilters={resetMarketingFilters}
        />
      }
    >
      <ContentLayout hasSecondaryMenu={false}>
        <CampaignsDashboardTable
          rows={rows}
          agencyLookup={agencyLookup}
          isPending={isPending}
          isAgenciesPending={isAgenciesPending}
          lastRowRef={lastRowRef}
          onCampaignRowClick={onCampaignRowClick}
          onSortLaunch={onSortLaunch}
        />
      </ContentLayout>
    </PageLayout>
  )
}

export default CampaignsDashboard
